import { useEffect, useState } from 'react'

import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { type HeadItemType, LoadingHavaianas, Pagination, Table, Typography } from 'components'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { toast } from 'react-toastify'
import { getOvInvoiceDetail } from 'services/manage_ov'
import type { ovInvoiceDetail } from 'services/manage_ov/interface'

import * as S from './styles'

type InvoiceDetailModalProps = {
  id: string
  open: boolean
  setOpen: (open: boolean) => void
}

const InvoiceDetailModal = ({ id, open, setOpen }: InvoiceDetailModalProps) => {
  const PAGE_SIZE = 10
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(true)
  const [invoiceDetail, setInvoiceDetail] = useState<ovInvoiceDetail[]>([])

  const closeAction = () => {
    setOpen(false)
  }

  const getInvoiceDetail = async () => {
    setLoading(true)
    try {
      const invoiceDetailsResponse = await getOvInvoiceDetail(id, page - 1, PAGE_SIZE)
      if (invoiceDetailsResponse.data.Materiais?.length) {
        const invoiceDetails: ovInvoiceDetail[] = invoiceDetailsResponse.data.Materiais
        setInvoiceDetail(invoiceDetails)

        const rowsCount = invoiceDetailsResponse.data.RowsCount
        const amountOfPages = Math.ceil(rowsCount / PAGE_SIZE)
        setTotalItems(rowsCount)
        setTotalPages(amountOfPages)
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes da fatura')
    } finally {
      setLoading(false)
    }
  }

  const getSizes = (): HeadItemType[] => {
    const sizes: string[] = []

    if (invoiceDetail.length) {
      invoiceDetail.forEach((item: ovInvoiceDetail) => {
        if (item.ItemNumeracao.length) {
          item.ItemNumeracao.forEach((size: { DescricaoTamanho: string; Quantidade: number }) => {
            if (!sizes.includes(size.DescricaoTamanho)) {
              sizes.push(size.DescricaoTamanho)
            }
          })
        }
      })

      sizes.sort()
    }

    return sizes.map((size) => ({
      columnName: size,
      label: size,
    }))
  }

  const getTableHead = () => {
    const tableHead: HeadItemType[] = [
      { columnName: 'Material', label: 'Material' },
      { columnName: 'Cor', label: 'Cor' },
      { columnName: 'QtdCor', label: 'Qtd. Cor' },
      { columnName: 'ValorItem', label: 'Valor Item' },
    ]

    const sizes = getSizes()
    tableHead.push(...sizes)

    return tableHead
  }

  const getTableData = () => {
    const tableData = []

    if (invoiceDetail.length) {
      const materialsData = invoiceDetail.map((item: ovInvoiceDetail) => {
        const sizesRow = getSizes().map((size) => {
          const foundSize = item.ItemNumeracao.find(
            (itemSize: { DescricaoTamanho: string; Quantidade: number }) =>
              itemSize.DescricaoTamanho === size.label,
          )

          return foundSize ? foundSize.Quantidade : <S.EmptySize>0</S.EmptySize>
        })

        return {
          content: [
            item.CodigoMaterial,
            item.CodigoCor,
            numberToDecimalString(item.QuantidadeTotalItem),
            formatToPTBRNumber(item.ValorTotalItem),
            ...sizesRow,
          ],
          id: `${item.CodigoMaterial}-${item.CodigoCor}`,
        }
      })

      tableData.push(...materialsData)
    }

    return tableData
  }

  useEffect(() => {
    getInvoiceDetail()
  }, [id, page])

  useEffect(() => {
    function handleCloseByKeyUpEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && open) {
        closeAction()
      }
    }

    window.addEventListener('keyup', handleCloseByKeyUpEscape)

    return () => {
      window.removeEventListener('keyup', handleCloseByKeyUpEscape)
    }
  }, [open, closeAction])

  const renderSummary = () => {
    return (
      <S.SummaryWrapper>
        <Pagination
          currentPage={page}
          itemsPerPage={PAGE_SIZE}
          numberOfItems={totalItems}
          numberOfPages={totalPages}
          setCurrentPage={setPage}
        />
        <S.SummaryRow>
          <S.SummaryItem>
            <Typography design="hav-default-02-text">Doc. Faturamento</Typography>
            <span>{id}</span>
          </S.SummaryItem>
          <S.SummaryItem>
            <Typography design="hav-default-02-text">Nota Fiscal</Typography>
            <span>{invoiceDetail[0].NumeroNotaFiscal}</span>
          </S.SummaryItem>
        </S.SummaryRow>
      </S.SummaryWrapper>
    )
  }

  return (
    <>
      {open && (
        <ModalContentLayout
          closeAction={closeAction}
          closeButton
          styleContent={{ maxHeight: '590px', minHeight: '295px', width: '80vw' }}
          title={<S.ModalTitle>Detalhes de documento</S.ModalTitle>}
        >
          {loading ? (
            <LoadingHavaianas />
          ) : (
            <>
              <Table bodyData={getTableData()} fixedHead headData={getTableHead()} rowHeight={32} />
              {renderSummary()}
            </>
          )}
        </ModalContentLayout>
      )}
    </>
  )
}

export default InvoiceDetailModal
